/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCommunity } from '../../../../../state/thunks/commenting';
// utils
import { url } from '../../../../../utils/urls';
// material
import { Switch, Group, IconControl, TextField } from '@insticator/insticator-ui';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller'
import ExpandableSection from '../../../../../material/expandablesection/expandablesection'
import SectionDescription from '../../../../../material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// content
import TopCommenters from './topcommenters/topcommenters';
import TopArticles from './toparticles/toparticles';
import Timeframe from './timeframe/timeframe';
import PlaceholderImages from './placeholderimages/placeholderimages'
// styles
import injectSheet from 'react-jss';
import styles from './communitysection_styles'

/* ========== ~~~~~~~~~~ COMMENTING CONFIG: COMMUINITY SECTION ~~~~~~~~~~ ========== */
const CommunitySection = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { 
    data: { community: configCommunity, commentingUnitUUID }, 
    isUpdating 
  } = useSelector(state => state.commenting.commentingConfig)

  const communityConfigInitialState = useMemo(() => ({
    ...configCommunity, 
    placeholderImageType: configCommunity.placeholderImageUrl ? 'custom' : 'standard', 
    title: configCommunity.title || '',
    placeholderImage: null
  }), [configCommunity])

  const [community, setCommunity] = useState({...communityConfigInitialState})
  const [isOpened, setIsOpened] = useState(false);

  const positionPreview = useMemo(() => configCommunity.position.toLowerCase().replace(/(^\w|_\w)/g, m => m.toUpperCase()).replace('_', '-'), [configCommunity])

  const isCustomImageEmpty = community.placeholderImageType === 'custom' && (!community.placeholderImage && !community.placeholderImageUrl)

  const isCustomTimeframeEmpty = community.topArticlesDaysTimeFrame === ''

  const isCommunityConfigInvalid = isCustomImageEmpty || isCustomTimeframeEmpty

  const handleCommunityConfigChange = (property, value) => {
    setCommunity({
      ...community,
      [property]: value
    })
  }

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async () => {
    const response = await dispatch(updateCommunity({communityConfigurationUUID: configCommunity.uuid, community, commentingUnitUUID}))
    setIsOpened(false)
  })

  const handleCancel = () => {
    setIsOpened(false)
    setCommunity(communityConfigInitialState)
  }

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(community) === JSON.stringify(communityConfigInitialState));
    return isFieldsChanged
  }, [community, communityConfigInitialState])

  const controller = (
    <ConfigController
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.community}
      disableSave={!isChanged || isCommunityConfigInvalid}
      title='Community'
      description={
        <SectionDescription
            main={`${positionPreview} Tab`}
            additional='Top articles, +2 more'
        />}
    />
  );

  return (
    <ExpandableSection
      className={`communitysection ${props.classes.communitysection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.community}
      disableSave={!isChanged || isCommunityConfigInvalid}
    >
      <SectionCard>
        <SectionGroup row>
          <SectionGroupDescription
            title={'Community'}
            description={'Community shows most popular discussions and top commenters on your website.'}
          />
          <Switch
            handleClick={() => handleCommunityConfigChange('isCommunityEnabled', !community.isCommunityEnabled)}
            on={community.isCommunityEnabled}
            variant='secondary'
          />
        </SectionGroup>
        { community.isCommunityEnabled &&
          <SectionGroup>
            <SectionGroupDescription
              title={'Position in the commenting unit'}
            />
            <Group name={'controls'}>
              <IconControl
                text={'In-Unit Tab'}
                selected={community.position === 'IN_UNIT'}
                image={`${url.image}/illustrations/illustration-In-unit-tab.svg`}
                value={'IN_UNIT'}
                handleSelect={value => handleCommunityConfigChange('position', value)}
              />
              <IconControl
                text={'Above Unit'}
                selected={community.position === 'ABOVE_UNIT'}
                image={`${url.image}/illustrations/illustration-above-unit.svg`}
                value={'ABOVE_UNIT'}
                handleSelect={value => handleCommunityConfigChange('position', value)}
              />
              <IconControl
                text={'Below Unit'}
                selected={community.position === 'BELOW_UNIT'}
                image={`${url.image}/illustrations/illustration-below-unit.svg`}
                value={'BELOW_UNIT'}
                handleSelect={value => handleCommunityConfigChange('position', value)}
              />
            </Group>
          </SectionGroup>
        }
        <SectionGroup>
          <h2>Title</h2>
          <TextField
            type={'text'}
            name={'communityTitle'}
            value={community.title}
            handleChange={e => handleCommunityConfigChange('title', e.target.value)}
            placeholder={'Popular in the community'}
          />
        </SectionGroup>

        <SectionGroup row>
          <SectionGroupDescription
            title={'Top articles'}
            description={'Define how many top articles should be loaded by default.'}
          />
          <Switch
            handleClick={() => handleCommunityConfigChange('showTopArticles', !community.showTopArticles)}
            on={community.showTopArticles}
            variant='secondary'
          />
        </SectionGroup>

        { community.showTopArticles &&
          <>
            <SectionGroup secondary>
              <SectionGroupDescription />
              <TopArticles
                section={'config'}
                topArticlesInitialLoadLimit={community.topArticlesInitialLoadLimit}
                onChange={value => handleCommunityConfigChange('topArticlesInitialLoadLimit', value)}
              />
            </SectionGroup>

            <SectionGroup secondary>
              <SectionGroupDescription title={'Top articles timeframe'} />
              <Timeframe
                section={'config'}
                topArticlesDaysTimeFrame={community.topArticlesDaysTimeFrame}
                onChange={value => handleCommunityConfigChange('topArticlesDaysTimeFrame', value)}
              />
            </SectionGroup>

            <SectionGroup secondary>
              <SectionGroupDescription title={'Placeholder images for the articles without a header image'} />
              { isOpened &&
                <PlaceholderImages
                  placeholderImageUrl={configCommunity.placeholderImageUrl}
                  onChange={value => handleCommunityConfigChange('placeholderImage', value)}
                  onSelectType={value => handleCommunityConfigChange('placeholderImageType', value)}
                />
              }
            </SectionGroup>
          </>
        }

        <SectionGroup row>
          <SectionGroupDescription
            title={'Top commenters'}
            description={'Define how many top commenters should be loaded by default.'}
          />
          <Switch
            handleClick={() => handleCommunityConfigChange('showTopCommenters', !community.showTopCommenters)}
            on={community.showTopCommenters}
            variant='secondary'
          />
        </SectionGroup>

        { community.showTopCommenters &&
          <SectionGroup secondary>
            <SectionGroupDescription />
            <TopCommenters
              topCommentersInitialLoadLimit={community.topCommentersInitialLoadLimit}
              onChange={value => handleCommunityConfigChange('topCommentersInitialLoadLimit', value)}
            />
          </SectionGroup>
        }
      </SectionCard>
    </ExpandableSection>
  )
})  

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CommunitySection;